import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { isRemoteApiEnabled } from '@/api/client'
import { env } from '@/config/env'
import { getSettings } from '@/services/settingsService'

/**
 * Store settings, fetched once at startup.
 *
 * These used to live only in VITE_ vars baked into the bundle at build time,
 * which meant the owner could not change the delivery fee, the brand name or
 * the support phone number without a rebuild and a redeploy — and the two
 * delivery numbers had to be kept in sync with the backend's own env vars by
 * hand, so when they drifted the cart quoted one fee and the courier collected
 * another. The API is the source of truth now.
 *
 * The env values remain the fallback, used before the fetch resolves and when
 * there is no backend configured at all. That keeps the header rendering a
 * brand name instead of a blank, and the cart a plausible number instead of ৳0,
 * on first paint.
 *
 * Not a hard dependency: a failed fetch leaves the fallback in place and is not
 * surfaced to the shopper, because the backend reprices the order anyway.
 */

const SettingsContext = createContext(null)

const FALLBACK = Object.freeze({
  deliveryFee: env.deliveryFee,
  deliveryFeeOutsideDhaka: env.deliveryFeeOutsideDhaka,
  freeDeliveryThreshold: env.freeDeliveryThreshold,
  brandName: env.brandName,
  brandTagline: env.brandTagline,
  supportPhone: env.supportPhone,
  whatsappNumber: env.whatsappNumber,
  messengerPageId: env.messengerPageId,
  contactEmail: env.contactEmail,
  promoVideoUrl: env.promoVideoUrl,
})

/**
 * Merge an API response over the fallback.
 *
 * Strings use `||` rather than `??` on purpose: the backend resolves a field
 * the owner has never filled in to an empty string, and an empty brand name
 * should fall back to the bundled default rather than render as a blank gap in
 * the header.
 */
function merge(remote) {
  if (!remote) return FALLBACK
  return {
    deliveryFee: Number(remote.deliveryFee ?? FALLBACK.deliveryFee),
    deliveryFeeOutsideDhaka: Number(
      remote.deliveryFeeOutsideDhaka ?? FALLBACK.deliveryFeeOutsideDhaka
    ),
    freeDeliveryThreshold: Number(
      remote.freeDeliveryThreshold ?? FALLBACK.freeDeliveryThreshold
    ),
    brandName: remote.brandName || FALLBACK.brandName,
    brandTagline: remote.brandTagline || FALLBACK.brandTagline,
    supportPhone: remote.supportPhone || FALLBACK.supportPhone,
    whatsappNumber: remote.whatsappNumber || FALLBACK.whatsappNumber,
    messengerPageId: remote.messengerPageId || FALLBACK.messengerPageId,
    contactEmail: remote.contactEmail || FALLBACK.contactEmail,
    // `??` not `||`: an empty string here is the owner's choice to have no
    // video, and must not fall back to the env default.
    promoVideoUrl: remote.promoVideoUrl ?? FALLBACK.promoVideoUrl,
  }
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(FALLBACK)
  // With no backend there is nothing to wait for, so the fallback *is* the
  // loaded state — derived here rather than set from the effect below.
  const [isLoaded, setIsLoaded] = useState(() => !isRemoteApiEnabled())

  useEffect(() => {
    if (!isRemoteApiEnabled()) return
    let cancelled = false
    getSettings()
      .then((remote) => {
        if (cancelled || !remote) return
        setSettings(merge(remote))
      })
      .catch(() => {
        // Keep the fallback. See the note above on why this is not an error.
      })
      .finally(() => {
        if (!cancelled) setIsLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const value = useMemo(
    () => ({
      ...settings,
      isLoaded,
      // Also merged — a bare setSettings here would drop the fallbacks and the
      // numeric coercion, which is how the admin screen could blank the header
      // after a save that left a field empty.
      refresh: () => getSettings().then((remote) => setSettings(merge(remote))),
    }),
    [settings, isLoaded]
  )

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>
}

export function useSettings() {
  const ctx = useContext(SettingsContext)
  if (!ctx) throw new Error('useSettings must be used inside <SettingsProvider>')
  return ctx
}
